import { useState, useCallback, useRef, useEffect } from "react";

import { questions, evaluateAssessment } from "./assessment";
import {
  Phase,
  Answers,
  QuestionId,
  AssessmentResult
} from "./types";

interface UseAiAssessmentReturn {
  phase: Phase;
  currentStep: number;
  totalSteps: number;
  answers: Answers;
  result: AssessmentResult | null;
  currentQuestion: (typeof questions)[number];
  handleStart: () => void;
  handleAnswer: (questionId: QuestionId, value: string) => void;
  handleNavigate: (direction: "prev" | "next" | "finish") => void;
  handleRestart: () => void;
}

export const useAiAssessment = (): UseAiAssessmentReturn => {
  const [phase, setPhase] = useState<Phase>("intro");
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const autoAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const totalSteps = questions.length;
  const currentQuestion = questions[currentStep];

  useEffect(
    () => () => {
      if (autoAdvanceRef.current) {
        clearTimeout(autoAdvanceRef.current);
      }
    },
    []
  );

  const handleStart = useCallback(() => {
    setPhase("questions");
    setCurrentStep(0);
  }, []);

  const handleAnswer = useCallback(
    (questionId: QuestionId, value: string) => {
      setAnswers((prev) => ({ ...prev, [questionId]: value }));

      if (autoAdvanceRef.current) {
        clearTimeout(autoAdvanceRef.current);
      }

      const isLast = currentStep === totalSteps - 1;

      autoAdvanceRef.current = setTimeout(() => {
        if (isLast) {
          const updated = { ...answers, [questionId]: value };
          const assessmentResult = evaluateAssessment(updated);

          setResult(assessmentResult);
          setPhase("results");
        } else {
          setCurrentStep((prev) => prev + 1);
        }
      }, 350);
    },
    [currentStep, totalSteps, answers]
  );

  const handleNavigate = useCallback(
    (direction: "prev" | "next" | "finish") => {
      if (autoAdvanceRef.current) {
        clearTimeout(autoAdvanceRef.current);
      }

      if (direction === "prev" && currentStep > 0) {
        setCurrentStep((prev) => prev - 1);
      } else if (
        direction === "next" &&
        currentStep < totalSteps - 1
      ) {
        setCurrentStep((prev) => prev + 1);
      } else if (direction === "finish") {
        const assessmentResult = evaluateAssessment(answers);

        setResult(assessmentResult);
        setPhase("results");
      }
    },
    [currentStep, totalSteps, answers]
  );

  const handleRestart = useCallback(() => {
    setPhase("intro");
    setCurrentStep(0);
    setAnswers({});
    setResult(null);
  }, []);

  return {
    phase,
    currentStep,
    totalSteps,
    answers,
    result,
    currentQuestion,
    handleStart,
    handleAnswer,
    handleNavigate,
    handleRestart
  };
};
